import events from "_src/engine/events";
import output from "_src/engine/output.js";
import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import { Geometry, GeometryBuffer } from "_src/engine/geometry.js";
import {
  primitiveType,
  bufferType,
  pixelBufferType,
  textureMinFilter,
  textureMagFilter
} from "_src/engine/constants";

import DefaultShader from "_src/shaders/default/default_shader";
import CircleShader from "_src/shaders/circle/circle";
import LineShader from "_src/shaders/line/line";
import windowManager from "_src/client/window_manager";
import renderGraph from "_src/render_graph/render_graph";
import appState from "_src/app_state";
import workspace from "_src/workspace";
import loader from "_src/loader";
import {lineColorType, shadingType} from "_src/constants";
import { screenUtils } from "_src/client/utils";
import config from "_src/config";
import lassoPng from "_assets/textures/lasso.png";

const CIRCLE_PIXEL_WIDTH = 8;
const LINE_TEXTURE_PERIOD = 20;

export default {

  sphere : null,
  plane : null,

  animated: false,
  stopped : true,

  init() {
    this._initSphere();
    this._initPlane();
    this._initContour();
    this.updateCircleRadius();
    this._initEvents();
  },

  setSelectionTypeEnabled(type, enabled) {

    switch (type) {
      case appState.ST_SPHERE:
        this._setSphereModeEnabled(enabled);
        break;
      case appState.ST_CUT_OFF_PLANE:
        this._setCircleModeEnabled(enabled);
        this._setPlaneModeEnabled(false);
        break;
      case appState.ST_CIRCLE:
        this._setCircleModeEnabled(enabled);
        break;
      case appState.ST_RECTANGLE:
        this._setRectangleModeEnabled(enabled);
        break;
      case appState.ST_LASSO:
        this._setLassoModeEnabled(enabled);
        break;
    }

  },

  setSphereVisibility(visibility) {
    this.sphere.setVisibility(visibility);
  },

  setPlaneVisibility(visibility) {
    this.plane.setVisibility(visibility);
  },

  updateSphereRadius() {
    if (this.sphere) {
      const virtualSphereRadius = appState.getStateValue("selectionSphereRadius");
      const scale = screenUtils.calculateObjectScale(virtualSphereRadius, 2, this.sphere.trans, workspace.getScene().camera);
      this.sphere.setScale(scale, scale, scale);
      updateSphereBounding(this.sphere);
    }
  },

  getSphereRadius() {
    if (this.sphere) {
      return this.sphere.scale[0];
    }

    return appState.getStateValue("selectionSphereRadius");
  },

  setPlaneSize(size) {
    this.plane.setScale(size, size, size);
    updateObjectBounding(this.plane);
  },

  setSphereLocation(location) {
    this.sphere.copyTranslation(location);
  },

  getSphereLocation(dest) {
    return this.sphere.getTranslation(dest);
  },

  setPlaneLocation(location) {
    this.plane.copyTranslation(location);
    updateObjectBounding(this.plane);
  },

  setPlaneRotation(rotation) {
    this.plane.copyRotation(rotation);
    updateObjectBounding(this.plane);
  },

  updateCircleRadius() {

    if (windowManager.getWindow(windowManager.MAIN)) {
      const maskPlane = this._getMaskScreenQuad();
      const interfacePlane = this._getInterfaceScreenQuad();

      const radius = appState.getStateValue("selectionCircleRadius");
      maskPlane.shader.setSelectionRadius(radius);
      interfacePlane.shader.setSelectionRadius(radius);
    }

  },

  startCircleLocation(x, y) {

    const maskPlane = this._getMaskScreenQuad();
    maskPlane.shader.setSecondPoint(x, y);

    this.setCircleLocation(x, y);

  },

  setCircleLocation(x, y) {

    const maskPlane = this._getMaskScreenQuad();
    const interfacePlane = this._getInterfaceScreenQuad();

    maskPlane.shader.setFirstPoint(x, y);
    interfacePlane.shader.setSelectionPoint(x, y);

    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame(false);

  },

  setMaskVisibility(visible) {

    const maskPlane = this._getMaskScreenQuad();
    maskPlane.setVisibility(visible);

  },

  setMaskSceneCleared(cleared) {

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const selectionPass = mainRenderGraph.getPass(renderGraph.SELECTION);
    const selectionMaskScene = selectionPass.getMainScene();

    selectionMaskScene.clearedColor = cleared;

  },

  setRectangleCorner(minx, miny, maxx, maxy) {

    const maskPlane = this._getMaskScreenQuad();
    maskPlane.shader.setRectangle(minx, miny, maxx, maxy);

    const contour = this._getСontour();
    this._setRectangleContourLine(contour, minx, miny, maxx, maxy);

  },

  animate(delta) {
    if(this._animateContour(delta)) {
      windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame(false);
    }
  },

  _initEvents() {

    this._updatePlaneBoundings = (object) => {
      if (object == this.plane) {
        updateObjectBounding(object);
      }
    }

    events.on("model_transformed", this._updatePlaneBoundings);
  },

  _setSphereModeEnabled(enabled) {
    this.setSphereVisibility(enabled);
  },

  _setPlaneModeEnabled(enabled) {
    this.setPlaneVisibility(enabled);
  },

  _setCircleModeEnabled(enabled) {

    if (enabled) {

      const maskPlane = this._getMaskScreenQuad();
      const interfacePlane = this._getInterfaceScreenQuad();

      interfacePlane.show();

      interfacePlane.shader.setWidth(CIRCLE_PIXEL_WIDTH);

      maskPlane.shader.setDirective("MODE", "CIRCLE");
      interfacePlane.shader.setDirective("MODE", "CIRCLE");

    }

    this.setMaskSceneCleared(enabled);
    this.setMaskVisibility(!enabled);

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    mainRenderGraph.setEnabled(renderGraph.SELECTION, enabled);

  },

  _setRectangleModeEnabled(enabled) {

    if (enabled) {

      const maskPlane = this._getMaskScreenQuad();
      const interfacePlane = this._getInterfaceScreenQuad();

      interfacePlane.hide();

      maskPlane.shader.setDirective("MODE", "RECT");

      this.resetRectangle();

    }

    this.setMaskSceneCleared(true);
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    mainRenderGraph.setEnabled(renderGraph.SELECTION, enabled);

  },

  _setLassoModeEnabled(enabled) {
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    mainRenderGraph.setEnabled(renderGraph.LASSO, enabled);
  },

  addLassoPoint(x, y) {

    const lassoPrimitive = this._getLassoPrimitive();
    const lassoContour = this._getСontour();
    const chunk = lassoPrimitive.chunks[0];

    if (chunk.isEmpty()) {
      this._initLassoChunk(lassoPrimitive, x, y);
    } else {
      this._addLassoTriangle(lassoPrimitive, x, y);
      this._addLassoContourLine(lassoContour, lassoPrimitive);
    }

  },

  resetCircle() {
    this.setMaskSceneCleared(true);
  },

  resetRectangle() {

    const maskPlane = this._getMaskScreenQuad();
    const contour = this._getСontour();

    maskPlane.shader.setRectangle(-1, -1, -1, -1);
    contour.chunks[0].cleanup();

  },

  resetLasso() {

    const lassoPrimitive = this._getLassoPrimitive();
    const lassoContour = this._getСontour();

    lassoPrimitive.chunks[0].cleanup();
    lassoContour.chunks[0].cleanup();

  },

  setContourAnimated(animated) {
    this.animated = animated;
  },

  setContourAnimationStopped(stopped) {
    this.stopped = stopped;
  },

  cleanup() {

    if (this.sphere) {
      this.sphere.cleanup();
      this.sphere = null;
    }

    if (this.plane) {
      this.plane.cleanup();
      this.plane = null;
    }

    events.off("model_transformed", this._updatePlaneBoundings);

  },

  _initSphere() {

    const sphereShader = new DefaultShader({transparent: true,
        shading: shadingType.SMOOTH
    });

    this.sphere = objFactory.createDrawable(sphereShader);
    this.sphere.setOpacity(0.5);

    this.sphere.addGeometryChunk(
      primitiveFactory.create(primitiveType.SPHERE, {
        widthSegments: 24,
        heightSegments: 12
      })
    );

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const interfaceScene = mainRenderGraph.getInterfaceScene();

    interfaceScene.add(this.sphere);

    this.updateSphereRadius();

    this.sphere.hide();

  },

  _initPlane() {

    const drawShader = new CircleShader({
      useBlur : false,
    });

    const fc = config.cutOffPlaneFColor;
    const bc = config.cutOffPlaneBColor;

    this.plane = objFactory.createDrawable(drawShader);
    this.plane.setDiffuseColor(fc.r, fc.g, fc.b);
    drawShader.setBackFaceColor(bc.r, bc.g, bc.b);
    this.plane.setOpacity(0.5);
    this.plane.addGeometryChunk(primitiveFactory.create(primitiveType.PLANE, {width: 1, height: 1}));

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const interfaceScene = mainRenderGraph.getInterfaceScene();

    interfaceScene.add(this.plane);

    this.plane.hide();

  },

  _initContour() {

    const lineShader = new LineShader({ lineWidth : 2, transparent : false,
        colorType : lineColorType.TEXTURE, screenSpace : true });

    const contour = objFactory.createDrawable(lineShader);
    contour.addGeometryChunk(primitiveFactory.create(primitiveType.LINE, {y1 : 1.0}));

    contour.setDiffuseColor(0, 0, 0);

    loader.load(lassoPng, loader.IMAGE).then((image) => {

      if (!image) {
        output.error(output.DEVELOPERS_ERROR, "Couldn't load the lasso texture.");
        return;
      }

      const minFilter = textureMinFilter.NEAREST;
      const magFilter = textureMagFilter.NEAREST;

      const texture = bufferFactory.create(pixelBufferType.T_RGBA, {width: image.width, height: image.height,
          minFilter, magFilter, immutable: true });
      texture.initMipMapLevelImage(0, image);

      contour.setTexture(texture, 0);

    }).catch((error) => {
      if (!loader.isLegalError(error)) {
        output.error(output.DEVELOPERS_ERROR, "Couldn't load the lasso texture. " + error);
      }
    });

    contour.chunks[0].cleanup();

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;

    const lassoPass = mainRenderGraph.getPass(renderGraph.LASSO);
    const lassoBlendScene = lassoPass.getLastScene();
    lassoBlendScene.add(contour);

    const selectionPass = mainRenderGraph.getPass(renderGraph.SELECTION);
    const selectionBlendScene = selectionPass.getLastScene();
    selectionBlendScene.add(contour);

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const viewport = mainWindow.getViewport();
    this.resize(viewport[2] - viewport[0]);

  },

  _getMaskScreenQuad() {

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const selectionPass = mainRenderGraph.getPass(renderGraph.SELECTION);
    const selectionMaskScene = selectionPass.getMainScene();

    return selectionPass.getScreenQuad(selectionMaskScene);

  },

  _getInterfaceScreenQuad() {

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const selectionPass = mainRenderGraph.getPass(renderGraph.SELECTION);
    const selectionInterfaceScene = selectionPass.getLastScene();

    return selectionPass.getScreenQuad(selectionInterfaceScene);

  },

  _getLassoPrimitive() {
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const lassoPass = mainRenderGraph.getPass(renderGraph.LASSO);
    const lassoScene = lassoPass.getMainScene();

    return lassoPass.getScreenQuad(lassoScene);

  },

  _getСontour() {

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const lassoPass = mainRenderGraph.getPass(renderGraph.LASSO);
    const lassoBlendScene = lassoPass.getLastScene();

    return lassoBlendScene.getChildren()[1];

  },

  _initRectangleChunk(contour, contourChunk, points, primitives, aspect) {
    const contourGeometry = primitiveFactory.create(primitiveType.LINE_ARRAY, { points, primitives, aspect });
    contour.setChunkGeometry(contourChunk, contourGeometry);
  },

  _updateRectangleChunk(chunk, points, aspect) {
    const positionsBuffer = chunk.geometry.getLayoutItem("a_position").getBuffer();
    const positionsNextBuffer = chunk.geometry.getLayoutItem("a_position_next").getBuffer();
    const uvBuffer = chunk.geometry.getLayoutItem("a_uv").getBuffer();

    let uvLength = 0;
    for (let i=0; i<4; i++) {
      const uvValue = Math.sqrt((points[(i+1)*3+0] - points[i*3+0]) * (points[(i+1)*3+0] - points[i*3+0]) +
        (points[(i+1)*3+1] - points[i*3+1]) * (points[(i+1)*3+1] - points[i*3+1]) / aspect / aspect +
        (points[(i+1)*3+2] - points[i*3+2]) * (points[(i+1)*3+2] - points[i*3+2]));

      positionsBuffer[i*12+0] = points[i*3+0];
      positionsBuffer[i*12+1] = points[i*3+1];
      positionsBuffer[i*12+2] = points[i*3+2];

      positionsNextBuffer[i*12+0] = points[(i+1)*3+0];
      positionsNextBuffer[i*12+1] = points[(i+1)*3+1];
      positionsNextBuffer[i*12+2] = points[(i+1)*3+2];

      uvBuffer[i*4+0] = uvLength;

      positionsBuffer[i*12+3] = points[(i+1)*3+0];
      positionsBuffer[i*12+4] = points[(i+1)*3+1];
      positionsBuffer[i*12+5] = points[(i+1)*3+2];

      positionsNextBuffer[i*12+3] = points[i*3+0];
      positionsNextBuffer[i*12+4] = points[i*3+1];
      positionsNextBuffer[i*12+5] = points[i*3+2];

      uvBuffer[i*4+1] = uvLength + uvValue;

      positionsBuffer[i*12+6] = points[i*3+0];
      positionsBuffer[i*12+7] = points[i*3+1];
      positionsBuffer[i*12+8] = points[i*3+2];

      positionsNextBuffer[i*12+6] = points[(i+1)*3+0];
      positionsNextBuffer[i*12+7] = points[(i+1)*3+1];
      positionsNextBuffer[i*12+8] = points[(i+1)*3+2];

      uvBuffer[i*4+2] = uvLength;

      positionsBuffer[i*12+9] = points[(i+1)*3+0];
      positionsBuffer[i*12+10] = points[(i+1)*3+1];
      positionsBuffer[i*12+11] = points[(i+1)*3+2];

      positionsNextBuffer[i*12+9] = points[i*3+0];
      positionsNextBuffer[i*12+10] = points[i*3+1];
      positionsNextBuffer[i*12+11] = points[i*3+2];

      uvBuffer[i*4+3] = uvLength + uvValue;

      uvLength += Math.sqrt((points[(i+1)*3+0] - points[i*3+0]) * (points[(i+1)*3+0] - points[i*3+0])
        + (points[(i+1)*3+1] - points[i*3+1]) * (points[(i+1)*3+1] - points[i*3+1]) / aspect / aspect)
    }

    chunk.markAttributeDirty("a_position");
    chunk.markAttributeDirty("a_position_next");
    chunk.markAttributeDirty("a_uv");
  },

  _initLassoChunk(primitive, x, y) {

    const positions = new Float32Array([x, y, 0]);
    const positionsItem = new GeometryBuffer({ buffer : positions.buffer, type : bufferType.F32_3 });

    const indices = new Uint32Array(3);
    const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : bufferType.UI32_1 });

    const layout = {
      "a_position" : positionsItem,
      "indices": indicesItem
    };

    const geometry = new Geometry({
      bbox     : [0,0,0,0,0,0],
      layout   : layout,
    });

    primitive.setChunkGeometry(primitive.chunks[0], geometry);

  },

  _addLassoTriangle(primitive, x, y) {

    const chunk = primitive.chunks[0];
    const geometry = chunk.geometry;

    const positionsItem = geometry.getLayoutItem("a_position");
    const indicesItem = geometry.getLayoutItem("indices");

    const positions = positionsItem.getBuffer();
    const indices = indicesItem.getBuffer();

    const newPositions = new Float32Array(positions.length + 3);
    const newIndices = new Uint32Array(indices.length + 1);

    newPositions.set(positions);
    newIndices.set(indices);

    const newIndex = positions.length / 3;

    newPositions[positions.length] = x;
    newPositions[positions.length + 1] = y;

    newIndices[indices.length] = newIndex;

    positionsItem.setBuffer(newPositions.buffer);
    indicesItem.setBuffer(newIndices.buffer);

    chunk.markAttributeDirty("a_position");
    chunk.markAttributeDirty("indices");

  },

  _addLassoContourLine(contour, lasso) {

    const lassChunk = lasso.chunks[0];
    const lassoGeometry = lassChunk.geometry;

    const lassoPositionsItem = lassoGeometry.getLayoutItem("a_position");
    const positions = lassoPositionsItem.getBuffer();

    if (positions.length < 6) {
      return;
    }

    const points = [positions[0], positions[1], 0];
    const primitives = [];

    for (let i = 1; i < positions.length / 3; ++i) {
      primitives.push(i - 1, i);
      points.push(positions[3 * i], positions[3 * i + 1], 0);
    }
    primitives.push(0, positions.length / 3 - 1);

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const viewport = mainWindow.getViewport();
    const aspect = (viewport[2] - viewport[0]) / (viewport[3] - viewport[1]);

    const contourChunk = contour.chunks[0];
    const contourGeometry = primitiveFactory.create(primitiveType.LINE_ARRAY, { points, primitives, aspect });

    contour.setChunkGeometry(contourChunk, contourGeometry);

  },

  _setRectangleContourLine(contour, minx, miny, maxx, maxy) {

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const viewport = mainWindow.getViewport();
    const aspect = (viewport[2] - viewport[0]) / (viewport[3] - viewport[1]);

    const primitives = [0, 1, 1, 2, 2, 3, 3, 0];
    const points = [minx, miny, 0, maxx, miny, 0, maxx, maxy, 0, minx, maxy, 0, minx, miny, 0];

    const contourChunk = contour.chunks[0];

    if (contourChunk.isEmpty()) {
      this._initRectangleChunk(contour, contourChunk, points, primitives, aspect);
    } else {
      this._updateRectangleChunk(contourChunk, points, aspect);
    }
  },

  _animateContour(delta) {

    if (!this.animated) {
      return false;
    }

    if (this.stopped) {
      this.stopped = false;
      return false;
    }

    const lassoContour = this._getСontour();
    const chunk = lassoContour.chunks[0];

    if (chunk.isEmpty()) {
      return false;
    }

    const shader = lassoContour.shader;

    let offset = shader.getTetxureOffset();

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const viewport = mainWindow.getViewport();
    const width = viewport[2] - viewport[0];
    const pixelRatio = screenUtils.getPixelRatio();

    offset += 0.1 * 1000 * delta / width * pixelRatio;
    offset = offset - Math.floor(offset);

    shader.setTextureOffset(offset);

    return true;

  },

  resize(width) {

    if (windowManager.getWindow(windowManager.MAIN)) {

      const contour = this._getСontour();
      const shader = contour.shader;

      const pixelRatio = screenUtils.getPixelRatio();

      shader.setTexturePeriod(LINE_TEXTURE_PERIOD / width * pixelRatio);

    }

  }

}

function updateObjectBounding(object) {
  object.updateBounding();
  events.emit("interface_bounding_changed");
}

function updateSphereBounding(sphere) {
  sphere.minmaxWorld[0] = sphere.trans[0] - sphere.scale[0];
  sphere.minmaxWorld[1] = sphere.trans[1] - sphere.scale[0];
  sphere.minmaxWorld[2] = sphere.trans[2] - sphere.scale[0];
  sphere.minmaxWorld[3] = sphere.trans[0] + sphere.scale[0];
  sphere.minmaxWorld[4] = sphere.trans[1] + sphere.scale[0];
  sphere.minmaxWorld[5] = sphere.trans[2] + sphere.scale[0];

  events.emit("interface_bounding_changed");
}
